import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from '../components/ui/dialog';
import * as VisuallyHidden from '@radix-ui/react-visually-hidden';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { trackPixel } from '@/lib/metaPixel';
import axios from 'axios';
import { User, Youtube, FileText, ChevronRight, ChevronLeft, Check, Phone, MapPin, Loader2, CalendarDays } from 'lucide-react';
import SlotPicker from './SlotPicker';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, label: 'About You', icon: User },
  { id: 2, label: 'Your Interest', icon: Youtube },
  { id: 3, label: 'Pick a Time', icon: CalendarDays },
  { id: 4, label: 'Final Details', icon: FileText },
];

const TOPIC_IDEAS = [
  'Cooking & Recipes', 'Gardening', 'Spirituality & Meditation',
  'Storytelling', 'Finance & Investing', 'Health & Wellness',
  'History & Culture', 'Teaching & Education', 'Music & Art',
  'Travel & Adventure', 'Life Lessons', 'Other'
];

const empty = {
  name: '', phone: '', city: '', address: '', age: '',
  youtube_interest: '', has_youtube_channel: false,
  slot_date: '', slot_hour: null,
  email: '', additional_info: ''
};

const YouTuberSignupWizard = ({ open, onOpenChange, stats }) => {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({ ...empty });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [result, setResult] = useState(null);
  const [waitlisted, setWaitlisted] = useState(null);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const canNext = () => {
    if (step === 1) return form.name.trim() && form.phone.trim() && form.city.trim() && form.address.trim();
    if (step === 2) return form.youtube_interest.trim();
    if (step === 3) return form.slot_date && form.slot_hour !== null;
    if (step === 4) return true;
    return false;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/youtuber-program/signup`, {
        ...form,
        age: form.age ? parseInt(form.age) : null,
        email: form.email || null,
      });
      if (res.data?.waitlisted) {
        setWaitlisted({ city: form.city.trim(), message: res.data.message });
        trackPixel('Lead', { content_name: 'YouTuber Waitlist', content_category: 'YouTuberProgram', city: form.city.trim() });
      } else {
        setResult(res.data);
        setDone(true);
        trackPixel('Schedule', {
          content_name: 'YouTuber Signup',
          content_category: 'YouTuberProgram',
          value: res.data?.is_free_slot ? 0 : 799,
          currency: 'INR',
          city: form.city.trim(),
        });
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Something went wrong. Please try again.';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = (val) => {
    if (!val) setTimeout(() => { setStep(1); setForm({ ...empty }); setDone(false); setResult(null); setWaitlisted(null); }, 300);
    onOpenChange(val);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="wiz-modal wiz-yt-signup" data-testid="yt-signup-modal">
        <VisuallyHidden.Root><DialogTitle>YouTuber Program Signup</DialogTitle><DialogDescription>Sign up for the elder YouTuber program in 3 steps.</DialogDescription></VisuallyHidden.Root>

        {waitlisted ? (
          <div className="wiz-success" data-testid="yt-signup-waitlist">
            <div style={{ fontSize: 48, marginBottom: 12 }}>🌱</div>
            <h2>Coming soon to {waitlisted.city}!</h2>
            <p style={{ maxWidth: 420, margin: '0 auto 12px' }}>{waitlisted.message}</p>
            <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 18 }}>We've saved your details and will WhatsApp <strong>{form.phone}</strong> the moment we launch in your city.</p>
            <Button className="wiz-done-btn wiz-yt-accent-bg" onClick={() => handleClose(false)} data-testid="yt-waitlist-close">Got it</Button>
          </div>
        ) : done && result ? (
          <div className="wiz-success" data-testid="yt-signup-success">
            <div className="wiz-success-icon wiz-yt-accent"><Check size={40} strokeWidth={3} /></div>
            <h2>You're In!</h2>
            <p className="wiz-slot-highlight">
              {result.is_free_slot
                ? <><strong>Congratulations!</strong> You got a free slot (#{result.slot_number}). No charges!</>
                : <>You're signup #{result.slot_number}. Visits will be at &#8377;799 each.</>
              }
            </p>
            <p>We'll call you on <strong>{form.phone}</strong> to schedule your first DigiMitra visit.</p>
            <Button className="wiz-done-btn wiz-yt-accent-bg" onClick={() => handleClose(false)} data-testid="yt-done-btn">Done</Button>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="wiz-header wiz-yt-header">
              <h2>Join the YouTuber Program</h2>
              <p>{stats?.free_slots_remaining > 0 ? `${stats.free_slots_remaining} free slots remaining` : 'Rs 799 per visit'}</p>
            </div>

            {/* Progress */}
            <div className="wiz-progress" data-testid="yt-progress">
              {STEPS.map((s) => {
                const Icon = s.icon;
                const active = step === s.id;
                const isDone = step > s.id;
                return (
                  <div key={s.id} className={`wiz-step ${active ? 'active' : ''} ${isDone ? 'done' : ''}`}>
                    <div className="wiz-step-dot wiz-yt-dot">
                      {isDone ? <Check size={16} strokeWidth={3} /> : <Icon size={16} />}
                    </div>
                    <span className="wiz-step-text">{s.label}</span>
                  </div>
                );
              })}
              <div className="wiz-bar"><div className="wiz-bar-fill wiz-yt-bar" style={{ width: `${((step - 1) / 3) * 100}%` }} /></div>
            </div>

            {/* Body */}
            <div className="wiz-body">
              {step === 1 && (
                <div className="wiz-fields wiz-anim" data-testid="yt-step-1">
                  <div className="wiz-row">
                    <div className="wiz-field"><label>Your Name *</label><Input data-testid="yt-name-input" placeholder="e.g. Suresh Sharma" value={form.name} onChange={e => set('name', e.target.value)} autoFocus /></div>
                    <div className="wiz-field"><label><Phone size={14} /> Phone *</label><Input data-testid="yt-phone-input" type="tel" placeholder="e.g. 98765 43210" value={form.phone} onChange={e => set('phone', e.target.value)} /></div>
                  </div>
                  <div className="wiz-row">
                    <div className="wiz-field"><label><MapPin size={14} /> City *</label><Input data-testid="yt-city-input" placeholder="e.g. Delhi" value={form.city} onChange={e => set('city', e.target.value)} /></div>
                    <div className="wiz-field"><label>Age</label><Input data-testid="yt-age-input" type="number" placeholder="e.g. 65" value={form.age} onChange={e => set('age', e.target.value)} /></div>
                  </div>
                  <div className="wiz-field">
                    <label><MapPin size={14} /> Address *</label>
                    <Input data-testid="yt-address-input" placeholder="House no, street, area, pincode" value={form.address} onChange={e => set('address', e.target.value)} />
                  </div>
                </div>
              )}

              {step === 2 && (
                <div className="wiz-fields wiz-anim" data-testid="yt-step-2">
                  <p className="wiz-topic-label">Pick a topic or type your own</p>
                  <div className="wiz-topic-grid">
                    {TOPIC_IDEAS.map(t => (
                      <button type="button" key={t} className={`wiz-topic-pill ${form.youtube_interest === t ? 'selected' : ''}`} onClick={() => set('youtube_interest', t)} data-testid={`yt-topic-${t.toLowerCase().replace(/\s+/g, '-')}`}>
                        {t}
                      </button>
                    ))}
                  </div>
                  <div className="wiz-field" style={{ marginTop: 14 }}>
                    <label>Or describe your interest</label>
                    <Input data-testid="yt-interest-input" placeholder="e.g. Teaching young people about financial discipline" value={form.youtube_interest} onChange={e => set('youtube_interest', e.target.value)} />
                  </div>
                  <label className="wiz-checkbox">
                    <input type="checkbox" data-testid="yt-has-channel" checked={form.has_youtube_channel} onChange={e => set('has_youtube_channel', e.target.checked)} />
                    I already have a YouTube channel
                  </label>
                </div>
              )}

              {step === 3 && (
                <div className="wiz-fields wiz-anim" data-testid="yt-step-3">
                  <p className="wiz-topic-label">Schedule your first DigiMitra call</p>
                  <SlotPicker
                    date={form.slot_date}
                    onDateChange={(v) => set('slot_date', v)}
                    serviceType="youtuber"
                    selectedHour={form.slot_hour}
                    onSelectHour={(h) => set('slot_hour', h)}
                    accentClass="slot-accent-yt"
                    testIdPrefix="yt-slot"
                  />
                </div>
              )}

              {step === 4 && (
                <div className="wiz-fields wiz-anim" data-testid="yt-step-4">
                  <div className="wiz-field"><label>Email <span className="wiz-opt">optional</span></label><Input data-testid="yt-email-input" type="email" placeholder="e.g. suresh@gmail.com" value={form.email} onChange={e => set('email', e.target.value)} autoFocus /></div>
                  <div className="wiz-field"><label>Anything else you'd like us to know? <span className="wiz-opt">optional</span></label><Textarea data-testid="yt-info-input" placeholder="Tell us about yourself, your goals, your audience..." value={form.additional_info} onChange={e => set('additional_info', e.target.value)} rows={3} /></div>
                  <div className="wiz-summary">
                    <div className="wiz-summary-title">Your Signup Summary</div>
                    <div className="wiz-summary-row"><span>Name</span><strong>{form.name}</strong></div>
                    <div className="wiz-summary-row"><span>City</span><strong>{form.city}</strong></div>
                    <div className="wiz-summary-row"><span>Topic</span><strong>{form.youtube_interest}</strong></div>
                    <div className="wiz-summary-row"><span>Slot</span><strong>{form.slot_date} · {String(form.slot_hour).padStart(2,'0')}:00</strong></div>
                    <div className="wiz-summary-row"><span>Pricing</span><strong style={{ color: stats?.free_slots_remaining > 0 ? '#16a34a' : '#e5266a' }}>{stats?.free_slots_remaining > 0 ? 'FREE (first visit)' : '₹799 per visit'}</strong></div>
                  </div>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="wiz-footer">
              {step > 1 && <Button variant="ghost" className="wiz-back" onClick={() => setStep(step - 1)} data-testid="yt-back"><ChevronLeft size={18} /> Back</Button>}
              <div className="wiz-spacer" />
              {step < 4 ? (
                <Button className="wiz-next wiz-yt-accent-bg" disabled={!canNext()} onClick={() => setStep(step + 1)} data-testid="yt-next">Next <ChevronRight size={18} /></Button>
              ) : (
                <Button className="wiz-next wiz-yt-accent-bg" disabled={loading} onClick={handleSubmit} data-testid="yt-submit-btn">
                  {loading ? <><Loader2 size={18} className="dm-spin" /> Signing Up...</> : <>Confirm Signup <Check size={18} /></>}
                </Button>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default YouTuberSignupWizard;
